import type { ParamRef } from "./controls";

// One gesture for every parameter, everywhere (desktop + touch): press a chip
// and drag vertically to change its value. While dragging, a floating HUD shows
// the parameter name + value big under the pointer, so even a small chip is a
// precise control. A plain tap (no movement) falls through to `onTap` (e.g.
// select the param on the big context slider). Replaces the tiny horizontal
// range inputs that were almost unusable on phones.

const DRAG_RANGE_PX = 180; // vertical pixels for a full min→max sweep
const MOVE_THRESHOLD = 5;  // px before a press counts as a drag, not a tap

let hud: HTMLElement | null = null;
let hudLabel: HTMLElement | null = null;
let hudValue: HTMLElement | null = null;
let hudFill: HTMLElement | null = null;

function ensureHud(): void {
  if (hud) return;
  hud = document.createElement("div");
  hud.className = "param-hud";
  hudLabel = document.createElement("div");
  hudLabel.className = "ph-label";
  hudValue = document.createElement("div");
  hudValue.className = "ph-value";
  const bar = document.createElement("div");
  bar.className = "ph-bar";
  hudFill = document.createElement("i");
  bar.append(hudFill);
  hud.append(hudLabel, hudValue, bar);
  document.body.append(hud);
}

function showHud(ref: ParamRef, frac: number, x: number, y: number): void {
  ensureHud();
  hud!.classList.add("show");
  hudLabel!.textContent = ref.label;
  hudValue!.textContent = ref.fmt(ref.get());
  hudFill!.style.width = `${Math.round(frac * 100)}%`;
  const hw = hud!.offsetWidth || 120;
  const left = Math.min(window.innerWidth - hw - 8, Math.max(8, x - hw / 2));
  const top = Math.max(8, y - 84);
  hud!.style.transform = `translate(${left}px, ${top}px)`;
}

function hideHud(): void {
  hud?.classList.remove("show");
}

function toFrac(ref: ParamRef, v: number): number {
  const f = ref.scale === "log"
    ? Math.log(v / ref.min) / Math.log(ref.max / ref.min)
    : (v - ref.min) / (ref.max - ref.min);
  return Math.min(1, Math.max(0, f));
}

function toVal(ref: ParamRef, f: number): number {
  const raw = ref.scale === "log"
    ? ref.min * Math.pow(ref.max / ref.min, f)
    : ref.min + f * (ref.max - ref.min);
  const stepped = Math.round(raw / ref.step) * ref.step;
  return Math.min(ref.max, Math.max(ref.min, stepped));
}

/**
 * Make `el` a drag-to-adjust control for the ParamRef returned by `getRef`.
 * Vertical drag = change value (HUD readout follows); plain tap = `onTap`.
 */
export function attachParamDrag(el: HTMLElement, getRef: () => ParamRef, onTap?: () => void): void {
  el.style.touchAction = "none";
  let pid = -1;
  let y0 = 0;
  let f0 = 0;
  let moved = false;

  el.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    pid = e.pointerId;
    y0 = e.clientY;
    f0 = toFrac(getRef(), getRef().get());
    moved = false;
  });

  el.addEventListener("pointermove", (e) => {
    if (e.pointerId !== pid) return;
    const dy = y0 - e.clientY;
    if (!moved && Math.abs(dy) < MOVE_THRESHOLD) return;
    moved = true;
    const ref = getRef();
    const f = Math.min(1, Math.max(0, f0 + dy / DRAG_RANGE_PX));
    ref.set(toVal(ref, f));
    showHud(ref, f, e.clientX, e.clientY);
  });

  const end = (e: PointerEvent): void => {
    if (e.pointerId !== pid) return;
    pid = -1;
    hideHud();
    if (moved) {
      // Swallow the trailing synthetic click so a drag doesn't also "tap".
      const swallow = (ce: MouseEvent): void => {
        ce.stopPropagation();
        ce.preventDefault();
      };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 80);
    } else if (e.type === "pointerup") {
      onTap?.();
    }
  };
  el.addEventListener("pointerup", end);
  el.addEventListener("pointercancel", end);
}
